import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { HomeHero } from "../../components/marketing/HomeHero";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { LangChip } from "../../components/ui/LangChip";
import { gameShowcase, pairRoutes, translatorJourney } from "../../data/marketing";
import { fadeUp, spring, staggerContainer } from "../../utils/motion";
export function Home() {
  const reduce = useReducedMotion() ?? false;
  const item = fadeUp(reduce);
  return <>
      <HomeHero data-id="element-1796" data-mp="2uoknarmlageo" />

      {/* Pairs explainer */}
      <section aria-labelledby="pairs-title" className="border-y-3 border-line bg-surface" data-id="element-1797" data-mp="2yqxtti95uici">
        <motion.div variants={staggerContainer()} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-15% 0px"
      }} className="mx-auto grid max-w-6xl gap-12 px-5 py-20 md:grid-cols-[1fr_1.1fr] md:items-center md:px-8 md:py-28" data-id="element-1798" data-mp="2yssh337wmmjr">
          <div data-id="element-1799" data-mp="3s5kzttnmvx2e">
            <motion.h2 variants={item} id="pairs-title" className="text-[clamp(32px,4.5vw,56px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1800" data-mp="3s64i5bh74ngy">
              One language. Many ways in.
            </motion.h2>
            <p className="mt-5 max-w-[46ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1801" data-mp="5y4xk8wzkvgt">
              Learn Spanish from English — or from Russian, Italian or German. Every course is a pair, so explanations, examples and
              grammar notes are written for the language you actually think in. Progress is tracked per pair.
            </p>
            <div className="mt-8" data-id="element-1802" data-mp="tn5xf7rr6yah">
              <BrutalButton to="/auth/signup" variant="ink" data-id="element-1803" data-mp="op4h2ulppd2p">
                Find your pair <ArrowRightIcon className="h-4 w-4" data-id="element-1804" data-mp="or8hv3obdvyr" />
              </BrutalButton>
            </div>
          </div>
          <motion.div variants={item} className="pat-dots rounded-panel border-3 border-line bg-paper p-6 md:p-8" data-id="element-1805" data-mp="xpkje0qpye5z">
            <div className="flex items-center gap-4" data-id="element-1806" data-mp="3ri2a9u25pz2c">
              <LangChip code="es" size="lg" data-id="element-1807" data-mp="3rjdbbm47wspr" />
              <div data-id="element-1808" data-mp="apthg56rnmy">
                <p className="text-2xl font-bold tracking-[-0.01em]" data-id="element-1809" data-mp="cam307xwwi6">Spanish</p>
                <p className="text-sm text-ink-2" data-id="element-1810" data-mp="4cb3mtqc5ntg">Español · 48 units</p>
              </div>
            </div>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2" data-id="element-1811" data-mp="93szx4hez083">
              {pairRoutes.map((r, i) => <motion.li key={r.code} initial={reduce ? false : {
              opacity: 0,
              x: -12
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              ...spring.smooth,
              delay: reduce ? 0 : 0.15 + i * 0.06
            }} className="flex items-center gap-3 rounded-xl border-3 border-line bg-surface px-3 py-2.5" data-id="element-1812" data-mp="e1kuk69g10fz" data-mpi={i}>
                  <LangChip code={r.code} tone="tint" data-id="element-1813" data-mp="2pu9ownq778ai" />
                  <span className="text-[15px] font-semibold" data-id="element-1814" data-mp="2tutjbjjdzxsy">{r.label}</span>
                </motion.li>)}
            </ul>
            <p className="mt-5 text-sm text-ink-2" data-id="element-1815" data-mp="2mcc5j14h9wu6">…and 11 more base languages for other courses.</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Games strip */}
      <section aria-labelledby="games-title" className="py-20 md:py-28" data-id="element-1816" data-mp="2ckzay1raoskg">
        <div className="mx-auto max-w-6xl px-5 md:px-8" data-id="element-1817" data-mp="28jvyzqcdbgoi">
          <h2 id="games-title" className="max-w-[20ch] text-[clamp(32px,4.5vw,56px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1818" data-mp="3594ysxdnaxoi">
            Seven ways to practise, mixed into every lesson.
          </h2>
          <p className="mt-4 max-w-[52ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1819" data-mp="2w8f98beza3j8">
            Each lesson blends them, so you read, hear, say and build the same words in different ways until they stick.
          </p>
        </div>
        <ul className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 md:px-[max(2rem,calc((100vw_-_72rem)/2_+_2rem))]" data-id="element-1820" data-mp="1kubdp0sqs7c2">
          {gameShowcase.map((g, i) => {
          const Icon = g.icon;
          return <motion.li key={g.id} initial={reduce ? false : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-10% 0px"
          }} transition={{
            ...spring.smooth,
            delay: reduce ? 0 : i * 0.05
          }} className={`flex w-[260px] shrink-0 snap-start flex-col rounded-card border-3 border-line p-5 shadow-brutal ${i === 0 ? "tex-dots bg-brand text-white" : "bg-surface text-ink"}`} data-id="element-1821" data-mp="33p20822ccm4y" data-mpi={i}>
                <span className={`grid h-11 w-11 place-items-center rounded-xl border-3 border-line ${i === 0 ? "bg-accent text-night" : "bg-brand-tint text-ink"}`} data-id="element-1822" data-mp="3dfwmh6fl7lqe">
                  <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" data-id="element-1823" data-mp="221sx6djy2lro" />
                </span>
                <h3 className="mt-8 text-xl font-bold tracking-[-0.01em]" data-id="element-1824" data-mp="2715igtv63ks5">{g.title}</h3>
                <p className={`mt-auto pt-3 text-[15px] ${i === 0 ? "text-white/85" : "text-ink-2"}`} lang="es" data-id="element-1825" data-mp="1yjbwncgeepyd">
                  {g.example}
                </p>
              </motion.li>;
        })}
        </ul>
      </section>

      {/* Translator memory */}
      <section aria-labelledby="memory-title" className="px-5 pb-20 md:px-8 md:pb-28" data-id="element-1826" data-mp="28fovspuppw0i">
        <div className="mx-auto grid max-w-6xl gap-10 rounded-blob border-3 border-line bg-brand-tint p-6 md:grid-cols-[1fr_1.2fr] md:p-14" data-id="element-1827" data-mp="23jhu599cn6od">
          <div data-id="element-1828" data-mp="1auj7pzy71id9">
            <h2 id="memory-title" className="text-[clamp(30px,4vw,52px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1829" data-mp="1ausp4z56ah7c">
              A translator that remembers for you.
            </h2>
            <p className="mt-5 max-w-[44ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1830" data-mp="1krox93r5t1bd">
              Use PolyLexa like any translator. Everything you look up is saved to My Words and slipped into your lessons — so the words you
              actually needed are the ones you end up knowing.
            </p>
          </div>
          <ol className="relative space-y-6 border-l-3 border-line pl-8" data-id="element-1831" data-mp="2s4pu1sa2l4cg">
            {translatorJourney.map((s, i) => <motion.li key={s.id} initial={reduce ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-10% 0px"
          }} transition={{
            ...spring.smooth,
            delay: reduce ? 0 : i * 0.08
          }} className="relative" data-id="element-1832" data-mp="2x3186f7ee10h" data-mpi={i}>
                <span className={`absolute -left-[46px] top-0.5 grid h-7 w-7 place-items-center rounded-full border-3 border-line ${i === translatorJourney.length - 1 ? "bg-good text-night" : "bg-surface"}`} aria-hidden="true" data-id="element-1833" data-mp="2e586fg8ifx4o">
                  {i === translatorJourney.length - 1 && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3.5} data-id="element-1834" data-mp="2ggvzmm5qkv3" />}
                </span>
                <h3 className="text-lg font-bold" data-id="element-1835" data-mp="2ia5ras2hl7b">{s.title}</h3>
                <p className="mt-1 text-[15px] leading-relaxed text-ink-2" data-id="element-1836" data-mp="cjvzm86vx9fl">{s.body}</p>
              </motion.li>)}
          </ol>
        </div>
      </section>

      {/* Loescher */}
      <section aria-labelledby="content-title" className="border-y-3 border-line bg-surface" data-id="element-1837" data-mp="2t9oaq67b52p">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-[1.3fr_1fr] md:px-8 md:py-24" data-id="element-1838" data-mp="3uxloj72rr1r2">
          <div data-id="element-1839" data-mp="3q0vasqefsi8t">
            <p className="text-sm font-bold text-brand" data-id="element-1840" data-mp="3px7exklirjqx">Curriculum partner</p>
            <h2 id="content-title" className="mt-2 text-[clamp(30px,4vw,52px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1841" data-mp="3kzfkojxejljk">
              Built on textbooks teachers already trust.
            </h2>
            <p className="mt-5 max-w-[52ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1842" data-mp="3c0u60zv1wqo2">
              Most of our courses are adapted from Loescher Editore's language materials. The book you read in the app and the games you
              play come from the same chapters, aligned to the CEFR from A1 to C1.
            </p>
          </div>
          <dl className="divide-y-2 divide-line/10 self-end" data-id="element-1843" data-mp="1tja152to2qid">
            {[["CEFR-aligned", "A1 through C1, with clear levels per course pair."], ["Book + games", "Read a chapter, then practise exactly its words."], ["Written by teachers", "Grammar notes explain, not just list."]].map(([t, d]) => <div key={t} className="py-4" data-id="element-1844" data-mp="19s56cr5bz7o3">
                <dt className="text-[17px] font-bold" data-id="element-1845" data-mp="1es01j6w52jey">{t}</dt>
                <dd className="mt-1 text-[15px] text-ink-2" data-id="element-1846" data-mp="1etks6lfc1i7j">{d}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="cta-title" className="px-5 py-20 md:px-8 md:py-28" data-id="element-1847" data-mp="15qjrj098kjb9">
        <div className="tex-dots mx-auto max-w-5xl rounded-blob border-3 border-line bg-brand px-6 py-14 text-center text-white shadow-brutal-lg md:py-20" data-id="element-1848" data-mp="2v5q76i2wpfqp">
          <h2 id="cta-title" className="mx-auto max-w-[16ch] text-[clamp(34px,5vw,64px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1849" data-mp="3039v69pep6vu">
            Your first word takes thirty seconds.
          </h2>
          <p className="mx-auto mt-5 max-w-[44ch] text-[17px] text-white/90" data-id="element-1850" data-mp="3583lbu5vff9y">Free to start. Plus from €5.99 a month when you're hooked.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row" data-id="element-1851" data-mp="3eacv2htbf4ma">
            <BrutalButton to="/auth/signup" size="lg" data-id="element-1852" data-mp="3szisc08lekrj">
              Start learning free
            </BrutalButton>
            <Link to="/pricing" className="focus-ring rounded-lg px-2 py-2 text-[15px] font-semibold text-white underline decoration-2 underline-offset-4 hover:decoration-accent" data-id="element-1853" data-mp="3o1h7b9rlwbdf">
              Compare plans
            </Link>
          </div>
        </div>
      </section>
    </>;
}